import InstagramLogoIcon from 'assets/icons/InstagramLogoIcon';
import SelfSocialAccountsIcon from 'assets/icons/mono/SelfSocialAccountsIcon';
import SocialAccountsIcon from 'assets/icons/mono/SocialAccountsIcon';
import Loader from 'Components/Common/Loader';
import ifConfigured from 'hoc/ifConfigured';
import useDocumentTitle from 'hooks/useDocumentTitle';
import React, { useEffect, useState } from 'react'
import Avatar from 'react-avatar';
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal } from 'reactstrap';
import { enableMediaTracking as enableMediaTrackingAPI } from 'services/api/social-account';
import { fetchSocialAccountsThunk } from 'slices/auth/thunk';
import { markCompetitorsDataExpired } from 'slices/competitionAnalysis/reducer';
import { toggleAddSocialAccountOffcanvas, toggleLimitExceededModal } from 'slices/ui/reducer';
import { AppDispatch, RootState } from 'store'
import { formatNumber, getErrorMessage, textToTitleCase } from 'utils/helpers';

const SocialAccounts = () => {
    const socialAccounts = useSelector((state: RootState) => state.auth.social_accounts.items);
    const socialAccountsLoading = useSelector((state: RootState) => state.auth.social_accounts.loading);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [enablingIds, setEnablingIds] = useState<Set<string>>(new Set());
    const membershipData = useSelector((state: RootState) => state.membershipState.membership.data)
    const configurations = useSelector((state: RootState) => state.auth.configurations);
    useDocumentTitle("Social Accounts");


    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchSocialAccountsThunk({ force: false }));
    }, [dispatch]);

    const enableMediaTracking = async (e: any, socialAccountId: string) => {
        if (!socialAccountId || !selectedAgencyId) return;
        try {
            setEnablingIds(prev => new Set(prev).add(socialAccountId));
            await enableMediaTrackingAPI(selectedAgencyId, socialAccountId);
            await dispatch(fetchSocialAccountsThunk({ force: true }))
            await dispatch(markCompetitorsDataExpired())
            toast.success("Media tracking enabled!");
        } catch (error) {
            console.log('err', error);
            toast.error(getErrorMessage(error));
        } finally {
            setEnablingIds(prev => {
                const s = new Set(prev);
                s.delete(socialAccountId);
                return s;
            });
        }
    }

    if (socialAccountsLoading) {
        return <Loader />
    }
    return (
        <>
            <div className='page-container'>
                <div className="list-card">
                    <div className="list-card-header">
                        <h6 className="list-card-heading">Social Accounts</h6>
                        <button className="list-card-header-action" onClick={() => {
                            if(socialAccounts.length >= (membershipData?.subscription_features?.max_ig_accounts?.applicable_limit ?? 0)){
                                dispatch(toggleLimitExceededModal('max_ig_accounts'))
                                return;
                            }
                            dispatch(toggleAddSocialAccountOffcanvas(true))
                        }}>+ Add Account</button>
                    </div>
                    <span className="list-card-description">Connect your Instagram to track previous collaborations (even outside Influns) and view your brand insights.</span>
                    <div className="list-card-body">
                        <div className="list-card-list">
                            {
                                socialAccounts.map((account, i) => (
                                    <div className="list-card-list-item" key={i}>
                                        <div className="d-flex align-items-start align-items-md-center justify-content-between flex-column flex-md-row gap-3">
                                            <div className="d-flex flex-column gap-2">
                                                <Link to={`/social-account/${account.social_account.uuid}`}>
                                                    <div className="d-flex align-items center gap-2">
                                                        {
                                                            (account?.social_account?.profile_picture_url) ? (
                                                                <img className='social-account-img' src={account?.social_account?.profile_picture_url} alt="" />
                                                            ) : (
                                                                <Avatar name={account?.social_account?.handle ?? ''} size="4.375rem" round />
                                                            )
                                                        }
                                                        <div className={`d-flex flex-column ${account.status === 'unverified' ? 'justify-content-center' : 'justify-content-between'} gap-2`}>
                                                            <div className="d-flex flex-column" style={{ gap: '0.12rem' }}>
                                                                <span className="list-card-list-item-title">
                                                                    {
                                                                        account?.social_account?.platform === 'ig' && account.social_account?.ig?.name
                                                                    }
                                                                    {
                                                                        account?.social_account?.platform === 'yt' && account.social_account?.yt?.title
                                                                    }
                                                                </span>
                                                                <span className="list-card-list-item-subtitle"> {account?.social_account?.platform === 'ig' ? <InstagramLogoIcon /> : <i className='ri-youtube-fill fs-16px text-danger'></i>} @{account?.social_account?.handle}</span>
                                                            </div>
                                                            {
                                                                account.status !== 'unverified' &&
                                                                <div className="d-flex align-items-center gap-2 mt-2">
                                                                    <span className="account-status-badge">{account.status.toUpperCase()}</span>
                                                                </div>
                                                            }

                                                        </div>
                                                    </div>
                                                </Link>
                                                <div className="d-flex campaign-actions gap-2 flex-wrap">
                                                    {
                                                        account?.social_account?.platform === 'ig' &&
                                                        <>
                                                            {
                                                                account?.status === 'unverified' &&
                                                                <Link to={`/c/ig?social_account_id=${account.social_account.uuid}`} className="theme-btn theme-btn-md btn-primary-outlined">Connect</Link>
                                                            }
                                                            {
                                                                account?.status === 'inactive' &&
                                                                <Link to={`/c/ig?social_account_id=${account.social_account.uuid}`} className="theme-btn theme-btn-md btn-primary-outlined">Re-Connect</Link>

                                                            }
                                                            {
                                                                configurations?.agency_track_media === 'y' &&
                                                                <>
                                                                    {
                                                                        account?.track_media?.status === 'active' &&
                                                                        <Link to={`/track-media?social_account_id=${account.social_account.uuid}`} className="theme-btn theme-btn-md btn-primary-outlined">Track Media</Link>
                                                                    }
                                                                    {
                                                                        (!account?.track_media || account?.track_media?.status === 'inactive') &&
                                                                        <button onClick={(e) => enableMediaTracking(e, account.social_account.uuid)} className='theme-btn theme-btn-md btn-primary-outlined'>{enablingIds.has(account.social_account.uuid) ? "Please wait..." : "Enable Tracking"}</button>
                                                                    }
                                                                </>
                                                            }
                                                        </>
                                                    }
                                                </div>
                                            </div>

                                            <div className="profile-stats border-1">
                                                <div className="row text-center g-0">
                                                    <div className="col-4 stats-item p-2 px-3">
                                                        <h4 className="profile-stat-value mb-1">{account.social_account.platform === 'ig' ? formatNumber(account.social_account.ig?.media_count ?? 0) : formatNumber(account.social_account.yt?.video_count ?? 0)}</h4>
                                                        <p className="mb-0 profile-stat-label">{account.social_account.platform === 'ig' ? 'Posts' : 'Videos'}</p>
                                                    </div>

                                                    <div className="col-4 stats-item border-start border-end p-2 px-3">
                                                        <h4 className="profile-stat-value mb-1">{account.social_account.platform === 'ig' ? formatNumber(account.social_account.ig?.followers_count ?? 0) : formatNumber(account.social_account.yt?.subscriber_count ?? 0)}</h4>
                                                        <p className="mb-0 profile-stat-label">{account.social_account.platform === 'ig' ? 'Followers' : 'Subscribers'}</p>
                                                    </div>

                                                    <div className="col-4 stats-item p-2 px-3">
                                                        <h4 className="profile-stat-value mb-1">{account.social_account.platform === 'ig' ? formatNumber(account.social_account.ig?.follows_count ?? 0) : formatNumber(account.social_account.yt?.view_count ?? 0)}</h4>
                                                        <p className="mb-0 profile-stat-label">{account.social_account.platform === 'ig' ? 'Following' : 'Views'}</p>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            }
                            {
                                (socialAccounts.length === 0) &&
                                <>
                                    <div className="d-flex w-100 justify-content-center align-items-center">
                                        <div className="length-zero d-flex align-items-center justify-content-center flex-column gap-2 my-5">
                                            <h3>Currently you have not connected any social account!</h3>
                                            {/* <Link className='btn btn-gradient d-flex gap-2 align-items-center' to={{ pathname: '/c/ig' }}> <i className='mdi mdi-instagram fs-5'></i> Connect Instagram</Link> */}
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default SocialAccounts